'use client';

import '@rainbow-me/rainbowkit/styles.css';
import { useEffect } from 'react';
import { WagmiProvider, useReconnect } from 'wagmi';
import { RainbowKitProvider, lightTheme } from '@rainbow-me/rainbowkit';
import { QueryClientProvider } from '@tanstack/react-query';
import { config } from '../lib/wagmi';
import { queryClient } from '../lib/query-client';

// Custom theme with high z-index for modal
const customTheme = lightTheme();

// Reconnects wagmi when user returns to the tab (e.g. after approving in Trust Wallet on mobile Chrome)
function WalletReconnectOnFocus() {
  const { reconnect } = useReconnect();

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        reconnect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [reconnect]);

  return null;
}

export default function Web3Providers({ children }: { children: React.ReactNode }) {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider
          appInfo={{
            appName: 'AsetQu Hub',
            learnMoreUrl: 'https://asetquhub.com',
            disclaimer: ({ Text, Link }) => (
              <Text>
                By connecting, you agree to our <Link href='/terms'>Terms of Service</Link> and{' '}
                <Link href='/privacy'>Privacy Policy</Link>
              </Text>
            ),
          }}
          theme={customTheme}
          modalSize='compact'
          showRecentTransactions={true}
          coolMode={false}
        >
          <WalletReconnectOnFocus />
          {children}
          {/* Global style override for RainbowKit modal z-index */}
          <style>{`
            [data-rk] [role="dialog"] {
              z-index: 99999 !important;
            }
            [data-rk] [data-testid="rk-connect-modal-overlay"] {
              z-index: 99998 !important;
            }
          `}</style>
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}
